/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Button, Separator } from "@farmlands/shadcn-ui";
import { LogOut } from "lucide-react";
import { FC, type JSX } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import TestIds from "../../../../testIds";
import { LOGOUT_CE_USER_ROUTE, LOGOUT_ROUTE } from "../../../../util/routing";
import { selectIsCEUser } from "../../../azure-b2c/storage";
import AppVersion from "../AppVersion/AppVersion";
import ContactProfileHeader from "../ContactProfileHeader/ContactProfileHeader";
import FeatureLinks, { IFeatureLinksProps } from "../FeatureLinks/FeatureLinks";
import SelectedAccount from "../SelectedAccount/SelectedAccount";
import UserAgreements from "../UserAgreements/UserAgreements";

export interface IAccountMenuProps extends IFeatureLinksProps {
  enableLogout?: boolean;
  enableAccountSwitcher?: boolean;
}

const AccountMenu: FC<IAccountMenuProps> = ({
  enableAccountSwitcher,
  enableAccountDetails,
  enableQuotes,
  enableSeed,
  enableDeclarations,
  enableActivities,
  enableOrders,
  enableCards,
  enablePayments,
  enableStatements,
  enableLogout,
}: IAccountMenuProps): JSX.Element => {
  const navigate = useNavigate();
  const isCEUser = useSelector(selectIsCEUser);

  return (
    <div
      className="fl-word-break overflow-auto"
      data-testid={TestIds.feat.account.userAccount.accountMenu.component}
    >
      <ContactProfileHeader />

      <div className="flex flex-col gap-3 px-4 pt-4 pb-6">
        <SelectedAccount enableAccountSwitcher={enableAccountSwitcher} fullScreenModal />

        <Separator decorative={false} />

        <FeatureLinks
          enableAccountDetails={enableAccountDetails}
          enableQuotes={enableQuotes}
          enableSeed={enableSeed}
          enableDeclarations={enableDeclarations}
          enableActivities={enableActivities}
          enableOrders={enableOrders}
          enableCards={enableCards}
          enablePayments={enablePayments}
          enableStatements={enableStatements}
        />

        {enableLogout && (
          <>
            <Separator decorative={false} />

            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid={TestIds.feat.account.userAccount.featureLinks.logout}
              onClick={() => navigate(isCEUser ? LOGOUT_CE_USER_ROUTE : LOGOUT_ROUTE)}
            >
              <LogOut aria-hidden size={16} />
              <span className="text-2xs leading-xs">Log Out</span>
            </Button>
          </>
        )}

        <div className="flex flex-col items-start gap-4">
          <div className="flex flex-col items-start gap-3">
            <UserAgreements />

            <AppVersion />
          </div>

          <p className="text-3xs leading-3xs font-medium text-neutral-500">
            Copyright &copy; Farmlands Co-operative Society Limited {new Date().getFullYear()} | All
            rights reserved
          </p>
        </div>
      </div>
    </div>
  );
};

export default AccountMenu;
